import { store } from '@/app/store';
import {
   resetCallkitData,
   setIncomingCallDetails,
   initializeParticipants,
   updateParticipantStatus,
   removeParticipantFromCall,
   setCallType,
   updateCallSwitch,
   setCallScreenStatus,
   addParticipantToCall,
   setCallLink,
   setCallMode,
   setMissedCallCount,
   triggerCallLogRefresh,
   swapSpeakerIntoVisible,
   setActiveSpeakerId,
} from '../model/callkitDataSlice';
import { isEnabled } from '../subFeatureRegistry';
import {
   CALL_SWITCH_ACCEPT,
   CALL_SWITCH_CANCEL,
   CALL_MODES,
   CALL_TYPE,
   CALLKIT_CALLSCREENSTATUS,
   CALLKIT_CALLSTATUS,
   CALL_LINK_UPDTAE,
   CALL_TYPE_UPDATE,
   CALL_MODE_UPDATE,
   CALL_KIT_SCREENSHARE_STATE,
   CALL_SWITCH_DECLINE,
   CLIENT_INITIATED_DISCONNECT,
} from '../constants';
import { getLocalUserName } from './helper';
import { getCallLogs, toggleCallKitVideo } from './sdkCallMethods';
import { CallMediaStore } from './CallMediaStore';
import { i18n } from '@/localization/i18n';
import { showErrorToast, showInfoToast, showWarningToast } from '@/shared/utils/toastMessage';
import {
   showIncomingCallNotification,
   closeIncomingCallNotification,
   showMissedCallNotification,
   isIncomingNotificationActive,
} from './browserNotification';
import { getUsersProfile } from '@/infrastructure/sdk/user';
import { setUserInfoMap } from '@/features/users/model';

// Guards against a participant being resurrected by their own trailing track-unpublish
// events. When someone disconnects, onCallStatusChanged schedules their removal, but the
// SDK's TrackUnsubscribed/TrackUnpublished events for that same participant can still
// reach onTrackUpdated afterward — and since they're no longer in ongoingCallParticipants,
// the "add missing participant" fallback there would otherwise add them right back.
const recentlyDisconnectedUsers = new Set();
const markRecentlyDisconnected = userId => {
   recentlyDisconnectedUsers.add(userId);
   setTimeout(() => recentlyDisconnectedUsers.delete(userId), 5000);
};

// Guards against the SDK/centrifugo redelivering the same terminal status (e.g. a failed
// backend status-update retry re-publishing an identical DISCONNECTED event a few hundred ms
// later) from re-fetching call logs and re-scheduling a log refresh once per duplicate.
const recentCallStatusEvents = new Set();
const isDuplicateCallStatusEvent = key => {
   if (recentCallStatusEvents.has(key)) return true;
   recentCallStatusEvents.add(key);
   setTimeout(() => recentCallStatusEvents.delete(key), 5000);
   return false;
};

const getParticipantAudioMuted = (userId, currentUserId, tracks) => {
   if (userId === currentUserId) return false;
   const audioTrack = tracks?.find(t => t.kind === 'audio' && (t.source || t.track?.source) !== 'screen_share_audio');
   return audioTrack ? audioTrack.isMuted : true;
};

const getParticipantVideoMuted = (userId, currentUserId, tracks) => {
   if (userId === currentUserId) {
      return store.getState().callkitData.callType !== CALL_TYPE.VIDEO;
   }
   const videoTrack = tracks?.find(t => t.kind === 'video' && (t.source || t.track?.source) !== 'screen_share');
   return videoTrack ? videoTrack.isMuted : true;
};

const syncOneToOneCallType = () => {
   const { ongoingCallParticipants, callType, callMode } = store.getState().callkitData;
   if (callMode !== CALL_MODES.ONETOONE) return;

   const activeParticipants = ongoingCallParticipants.filter(p => p.callStatus === CALLKIT_CALLSTATUS.CONNECTED);

   if (!isEnabled('videoSwitchEnable')) {
      // Group-call-like behavior: upgrade to video as soon as anyone's video is on,
      // never auto-downgrade back to audio once it has switched.
      if (callType === CALL_TYPE.AUDIO) {
         const anyVideoOn = activeParticipants.some(p => !p.isVideoMuted);
         if (anyVideoOn) {
            store.dispatch(setCallType(CALL_TYPE.VIDEO));
         }
      }
      return;
   }

   // Legacy behavior: downgrade to audio once both users have muted their video.
   if (callType === CALL_TYPE.VIDEO) {
      const allVideoMuted = activeParticipants.length > 0 && activeParticipants.every(p => p.isVideoMuted);
      if (allVideoMuted) {
         store.dispatch(setCallType(CALL_TYPE.AUDIO));
      }
   }
};

export const callkitCallbacks = {
   onIncomingCall: callData => {
      console.log('onIncomingCall triggered:', callData);
      const currentUserId = getLocalUserName();
      const { callScreenStatus } = store.getState().callkitData;

      if (callData.from === currentUserId || callScreenStatus !== CALLKIT_CALLSCREENSTATUS.IDLE) {
         console.log('Ignoring onIncomingCall: own call or user already in another call session');
         return;
      }

      const users = store.getState().userInfoData?.users || {};
      const remoteIdentities = (callData.participants || [])
         .map(p => p.identity)
         .filter(userId => userId !== currentUserId);
      const isGroupCall = String(callData.callMode).toUpperCase() === CALL_MODES.ONETOMANY;

      // We only display the caller and up to 2 other participants in the UI + count
      // So we only need to fetch profiles for those essential IDs.
      // For a group call, also fetch the group's own profile — its nickname is what
      // the browser notification shows instead of the caller's name.
      const essentialIds = Array.from(
         new Set([callData.from, ...remoteIdentities.slice(0, 2), ...(isGroupCall && callData.chatId ? [callData.chatId] : [])]),
      );
      const missingProfiles = essentialIds.filter(userId => userId && !users[userId]);

      if (missingProfiles.length > 0) {
         getUsersProfile(missingProfiles)
            .then(res => {
               if (res?.users) {
                  store.dispatch(setUserInfoMap(res.users));
                  showIncomingCallNotification(callData, store.getState().userInfoData.users);
               }
            })
            .catch(err => console.error('onIncomingCall: Error fetching participant profiles:', err));
      } else {
         showIncomingCallNotification(callData, users);
      }

      const participants = (callData.participants || []).map(p => {
         const isLocal = p.identity === currentUserId;
         const isVideoCall = callData.callType === CALL_TYPE.VIDEO;

         const participantData = {
            userId: p.identity,
            callStatus: p.callStatus,
            stream: null,
         };

         if (isLocal) {
            participantData.isAudioMuted = false;
            participantData.isVideoMuted = !isVideoCall;
         } else {
            const audioTrack = p.tracks?.find(
               t => t.kind === 'audio' && (t.source || t.track?.source) !== 'screen_share_audio',
            );
            const videoTrack = p.tracks?.find(
               t => t.kind === 'video' && (t.source || t.track?.source) !== 'screen_share',
            );

            participantData.isAudioMuted = audioTrack ? audioTrack.isMuted : true;
            participantData.isVideoMuted = videoTrack ? videoTrack.isMuted : true;
         }

         return participantData;
      });
      store.dispatch(initializeParticipants(participants));
      store.dispatch(setIncomingCallDetails(callData));
   },

   onActiveParticipants: participants => {
      console.log('onActiveParticipants triggered:', participants);
      // intentional no-op: initial participant list is populated via onIncomingCall / onCallStatusChanged
   },

   onParticipantConnected: newParticipant => {
      console.log('onParticipantConnected triggered:', newParticipant);
      const userId = newParticipant.identity;
      const currentUserId = getLocalUserName();
      // They're genuinely (re)joining — trailing track events from a past disconnect
      // should no longer be treated with suspicion.
      recentlyDisconnectedUsers.delete(userId);

      const { ongoingCallParticipants, callMode, callScreenStatus } = store.getState().callkitData;
      const participantExists = ongoingCallParticipants.some(p => p.userId === userId);
      const previousStatus = ongoingCallParticipants.find(p => p.userId === userId)?.callStatus;

      if (participantExists) {
         store.dispatch(
            updateParticipantStatus({
               userId: userId,
               callStatus: CALLKIT_CALLSTATUS.CONNECTED,
               isAudioMuted: getParticipantAudioMuted(userId, currentUserId, newParticipant.tracks),
               isVideoMuted: getParticipantVideoMuted(userId, currentUserId, newParticipant.tracks),
            }),
         );
      } else {
         store.dispatch(
            addParticipantToCall({
               userId: userId,
               callStatus: CALLKIT_CALLSTATUS.CONNECTED,
               isAudioMuted: getParticipantAudioMuted(userId, currentUserId, newParticipant.tracks),
               isVideoMuted: getParticipantVideoMuted(userId, currentUserId, newParticipant.tracks),
            }),
         );
      }

      // Show "X Joined" toast only when a new participant joins an already-established call.
      // callScreenStatus === CONNECTED ensures we don't fire for existing participants
      // when we ourselves are joining. Skip reconnects (RECONNECTING → CONNECTED).
      if (
         userId !== currentUserId &&
         callScreenStatus === CALLKIT_CALLSCREENSTATUS.CONNECTED &&
         previousStatus !== CALLKIT_CALLSTATUS.RECONNECTING &&
         previousStatus !== CALLKIT_CALLSTATUS.CONNECTED
      ) {
         const users = store.getState().userInfoData?.users || {};
         const name = users[userId]?.nickName || userId;
         if (callMode === CALL_MODES.MEET) {
            showInfoToast(i18n.t('common.MEET_JOINED', { name }));
         } else if (callMode === CALL_MODES.ONETOMANY) {
            const alreadyConnectedRemote = ongoingCallParticipants.find(
               p => p.userId !== currentUserId && p.userId !== userId && p.callStatus === CALLKIT_CALLSTATUS.CONNECTED,
            );
            if (alreadyConnectedRemote) {
               showInfoToast(i18n.t('common.MEET_JOINED', { name }));
            }
         }
      }
   },

   onParticipantDisConnected: participant => {
      console.log('onParticipantDisConnected triggered:', participant);
      // Actual removal is driven by onCallStatusChanged → DISCONNECTED status. This just
      // marks them so trailing track-unpublish events for this participant don't add them back.
      if (participant?.identity) markRecentlyDisconnected(participant.identity);
   },

   onRecordingStatusChanged: status => {
      console.log('onRecordingStatusChanged triggered:', status);
      // intentional no-op: recording UI not implemented
   },

   onRoomMessage: data => {
      console.log('onRoomMessage triggered:', data);
      if (data?.type === CALL_LINK_UPDTAE) {
         store.dispatch(setCallLink(data?.message?.callLink));
      } else if (data?.type === CALL_TYPE_UPDATE) {
         store.dispatch(setCallType(data?.message?.callType));
         if (data?.message?.callType === CALL_TYPE.VIDEO && isEnabled('videoSwitchEnable')) {
            toggleCallKitVideo(true);
         }
      } else if (data?.type === CALL_MODE_UPDATE) {
         store.dispatch(setCallMode(data?.message?.callMode));
      } else if (data?.message === 'timeout') {
         const { callScreenStatus, ongoingCallParticipants } = store.getState().callkitData;
         const currentUserId = getLocalUserName();
         const localUser = (ongoingCallParticipants || []).find(p => p.userId === currentUserId);
         const isConnectingState = localUser?.callStatus === CALLKIT_CALLSTATUS.TRY_TO_CONNECT;

         if (callScreenStatus !== CALLKIT_CALLSCREENSTATUS.IDLE && isConnectingState) {
            showWarningToast(`${i18n.t('common.INTERNET_ERROR_MESSAGE')}`);
            store.dispatch(resetCallkitData());
         }
      }
   },

   onRoomConnectionFailure: error => {
      console.log('onRoomConnectionFailure triggered:', error);
      // Self-initiated disconnects (user cancelled while connecting) aren't network errors —
      // the makeCall error callback shows the user-facing "call ended" toast for that case.
      if (error?.message !== CLIENT_INITIATED_DISCONNECT) {
         showErrorToast(i18n.t('common.INTERNET_ERROR_MESSAGE'));
      }
      store.dispatch(resetCallkitData());
   },

   onCallSwitch: callData => {
      console.log('onCallSwitch triggered:', callData);
      if (
         callData?.switchAction === CALL_SWITCH_CANCEL ||
         callData?.switchAction === CALL_SWITCH_ACCEPT ||
         callData?.switchAction === CALL_SWITCH_DECLINE
      ) {
         store.dispatch(updateCallSwitch({ switchAction: null }));
         if (callData?.switchAction === CALL_SWITCH_ACCEPT) {
            store.dispatch(setCallType(CALL_TYPE.VIDEO));
            toggleCallKitVideo(true);
         }
      } else {
         store.dispatch(updateCallSwitch(callData));
      }
   },

   onActiveSpeakerChange: speaker => {
      const { audioLevel, identity: userId } = speaker;
      if (audioLevel > 0.1) {
         store.dispatch(setActiveSpeakerId(userId));
         store.dispatch(swapSpeakerIntoVisible({ userId, localUser: getLocalUserName() }));
      }
   },

   onAudioTrackMuted: (participantInfo, isMuted) => {
      console.log('onAudioTrackMuted triggered:', participantInfo, isMuted);
      store.dispatch(updateParticipantStatus({ userId: participantInfo.identity, isAudioMuted: isMuted }));
   },

   onVideoTrackMuted: (participantInfo, isMuted) => {
      console.log('onVideoTrackMuted triggered:', participantInfo, isMuted);
      store.dispatch(updateParticipantStatus({ userId: participantInfo.identity, isVideoMuted: isMuted }));
      syncOneToOneCallType();
   },

   onConnectionQualityChanged: (userId, quality) => {
      console.log('onConnectionQualityChanged triggered:', userId, quality);
      if (!userId) return;

      const exists = store.getState().callkitData.ongoingCallParticipants.some(p => p.userId === userId);
      if (!exists) return;

      store.dispatch(updateParticipantStatus({ userId, connectionQuality: quality }));
   },

   onTrackUpdated: trackInfo => {
      console.log('onTrackUpdated triggered:', trackInfo);
      const userId = trackInfo.identity;
      const isLocal = trackInfo.isLocalUser;
      const updates = { userId };

      const audioTrack = (trackInfo.tracks || []).find(
         t => t.kind === 'audio' && (t.source || t.track?.source) !== 'screen_share_audio',
      );
      const videoTrack = (trackInfo.tracks || []).find(
         t => t.kind === 'video' && (t.source || t.track?.source) !== 'screen_share',
      );

      // For remote participants, if a track is missing from the delta info, do not forcefully mute it
      if (!isLocal) {
         if (audioTrack !== undefined) updates.isAudioMuted = audioTrack.isMuted;
         if (videoTrack !== undefined) updates.isVideoMuted = videoTrack.isMuted;
      } else {
         if (audioTrack) updates.isAudioMuted = audioTrack.isMuted;
         if (videoTrack) updates.isVideoMuted = videoTrack.isMuted;
      }

      trackInfo.tracks?.forEach(t => {
         const isScreenShare = (t.source || t.track?.source) === 'screen_share';
         const isRemoved =
            t.type === 'remove' ||
            trackInfo.type === 'remove' ||
            t.action === 'remove' ||
            trackInfo.action === 'remove';

         if (t.track && !isRemoved) {
            const exists = store.getState().callkitData.ongoingCallParticipants.some(p => p.userId === userId);
            if (isScreenShare) {
               const screenUserId = userId + '-screen';
               CallMediaStore.addTrack(screenUserId, t.track, 'video');
               const screenExists = store.getState().callkitData.ongoingCallParticipants.some(p => p.userId === screenUserId);
               if (!screenExists) {
                  console.log('screenshare connected');
                  store.dispatch(
                     addParticipantToCall({
                        userId: screenUserId,
                        callStatus: CALLKIT_CALLSTATUS.CONNECTED,
                        isAudioMuted: true,
                        isVideoMuted: false,
                     }),
                  );
               }else {
                  store.dispatch(
                     updateParticipantStatus({
                        userId: screenUserId,
                        trackUpdated: Date.now(),
                        isVideoMuted: t.track.isMuted !== undefined ? t.track.isMuted : false,
                        callStatus: CALLKIT_CALLSTATUS.CONNECTED, // Ensure status is resumed on re-publication
                     }),
                  );
               }
            } else if (recentlyDisconnectedUsers.has(userId)) {
               // Trailing TrackUnsubscribed/TrackUnpublished events for a participant who
               // just disconnected — ignore entirely so we don't resurrect them or flip
               // their status back to CONNECTED before the scheduled removal runs.
            } else {
               const trackType = t.kind;
               CallMediaStore.addTrack(userId, t.track, trackType);
               if (!exists) {
                  // Track update arrived before this participant was added to the call
                  // (e.g. onParticipantConnected hasn't fired yet) — add them now with
                  // their real mute state from this update, not a hardcoded guess.
                  store.dispatch(
                     addParticipantToCall({
                        userId: userId,
                        callStatus: CALLKIT_CALLSTATUS.CONNECTED,
                        isAudioMuted: updates.isAudioMuted ?? true,
                        isVideoMuted: updates.isVideoMuted ?? true,
                     }),
                  );
               }
               updates.trackUpdated = Date.now(); // Force UI update
               updates.callStatus = CALLKIT_CALLSTATUS.CONNECTED; // Ensure primary status resumes
            }
         } else if (isScreenShare) {
            const screenUserId = userId + '-screen';
            store.dispatch(removeParticipantFromCall(screenUserId));
            CallMediaStore.removeTrack(screenUserId, 'video');
         }
      });

      // Cleanup fallback: If the screenshare is completely dropped/unpublished, it will not appear in the tracks array.
      if (!isLocal) {
         const hasScreenShareTrack = (trackInfo.tracks || []).some(
            t => (t.source || t.track?.source) === 'screen_share',
         );

         if (!hasScreenShareTrack) {
            const screenUserId = userId + '-screen';
            const screenExists = store
               .getState()
               .callkitData.ongoingCallParticipants.some(p => p.userId === screenUserId);

            if (screenExists) {
               console.log('Screenshare track missing from updates. Removing from UI.', screenUserId);
               store.dispatch(removeParticipantFromCall(screenUserId));
               CallMediaStore.removeTrack(screenUserId, 'video');
            }
         }
      }

      if (Object.keys(updates).length > 1) {
         store.dispatch(updateParticipantStatus(updates));
         syncOneToOneCallType();
      }
   },

   onCallStatusChanged: async data => {
      const userId = data?.identity;
      const currentUserId = getLocalUserName();
      const ongoingCallParticipantsBefore = store.getState().callkitData.ongoingCallParticipants;
      const oldStatus = ongoingCallParticipantsBefore.find(p => p.userId === userId)?.callStatus;

      if (data?.callStatus === CALLKIT_CALLSTATUS.RECONNECTING && oldStatus !== CALLKIT_CALLSTATUS.CONNECTED) {
         console.log(`onCallStatusChanged: ignoring RECONNECTING for ${userId} as previous status was ${oldStatus}`);
         return;
      }

      store.dispatch(updateParticipantStatus({ userId: userId, callStatus: data?.callStatus }));

      // Synchronize status with screenshare tile if it exists
      const screenUserId = userId + '-screen';
      const screenParticipant = ongoingCallParticipantsBefore?.find(p => p.userId === screenUserId);
      if (screenParticipant) {
         store.dispatch(updateParticipantStatus({ userId: screenUserId, callStatus: data?.callStatus }));
      }

      if (userId != currentUserId && data?.callStatus === CALLKIT_CALLSTATUS.CONNECTED) {
         const previousLocalStatus = ongoingCallParticipantsBefore.find(p => p.userId === currentUserId)?.callStatus;
         if (previousLocalStatus === CALLKIT_CALLSTATUS.CONNECTED) {
            closeIncomingCallNotification();
            store.dispatch(setCallScreenStatus(CALLKIT_CALLSCREENSTATUS.CONNECTED));
         }
      } else if (
         data?.callStatus === CALLKIT_CALLSTATUS.CALLING ||
         data?.callStatus === CALLKIT_CALLSTATUS.RINGING ||
         data?.callStatus === CALLKIT_CALLSTATUS.CONNECTING ||
         data?.callStatus === CALLKIT_CALLSTATUS.TRY_TO_CONNECT ||
         data?.callStatus === CALLKIT_CALLSTATUS.ON_HOLD ||
         data?.callStatus === CALLKIT_CALLSTATUS.RECONNECTING
      ) {
         if (data?.callStatus === CALLKIT_CALLSTATUS.RINGING && userId !== currentUserId) {
            const users = store.getState().userInfoData?.users || {};
            const callkitData = store.getState().callkitData;
            if (callkitData.callScreenStatus === CALLKIT_CALLSCREENSTATUS.IDLE) {
               showIncomingCallNotification(
                  {
                     from: userId,
                     callType: callkitData.callType,
                     callMode: callkitData.callMode,
                     callId: callkitData.incomingCallDetails?.callId,
                     callerNickName: users[userId]?.nickName || userId,
                     groupId: callkitData.groupId,
                  },
                  users,
               );
            }
         }
         const isVideoCall = store.getState().callkitData.callType === CALL_TYPE.VIDEO;
         const isLocal = userId === currentUserId;

         // Capture mute state BEFORE any dispatches to preserve user-set mute state through reconnection
         const currentParticipant = store.getState().callkitData.ongoingCallParticipants.find(p => p.userId === userId);
         const preservedAudioMuted = isLocal ? (currentParticipant?.isAudioMuted ?? false) : true;
         const preservedVideoMuted = isLocal ? (currentParticipant?.isVideoMuted ?? !isVideoCall) : true;

         store.dispatch(
            addParticipantToCall({
               userId: userId,
               callStatus: data?.callStatus,
               isAudioMuted: preservedAudioMuted,
               isVideoMuted: preservedVideoMuted,
            }),
         );
         store.dispatch(
            updateParticipantStatus({
               userId: userId,
               callStatus: data?.callStatus,
               isAudioMuted: preservedAudioMuted,
               isVideoMuted: preservedVideoMuted,
            }),
         );
      } else if (
         data?.callStatus === CALLKIT_CALLSTATUS.DISCONNECTED ||
         data?.callStatus === CALLKIT_CALLSTATUS.ENGAGED ||
         data?.callStatus === CALLKIT_CALLSTATUS.REJECTED ||
         data?.callStatus === CALLKIT_CALLSTATUS.TIMEOUT
      ) {
         // Belt-and-suspenders alongside onParticipantDisConnected — some disconnect paths
         // (rejected/timeout/engaged) don't fire that callback at all.
         if (userId !== currentUserId) markRecentlyDisconnected(userId);

         // Keyed on userId alone, NOT userId+status: the SDK can resolve one missed call
         // through more than one terminal status (e.g. a TIMEOUT immediately followed by a
         // DISCONNECTED for the same participant), and each would otherwise independently
         // pass the checks below and double the fetch/refresh — same as an exact repeat would.
         const isDuplicateStatusEvent = isDuplicateCallStatusEvent(userId);

         if (!isDuplicateStatusEvent && isEnabled('missedCallLog')) {
            const { callScreenStatus } = store.getState().callkitData;
            if (
               callScreenStatus === CALLKIT_CALLSCREENSTATUS.INCOMING &&
               (data?.callStatus === CALLKIT_CALLSTATUS.DISCONNECTED || data?.callStatus === CALLKIT_CALLSTATUS.TIMEOUT)
            ) {
               getCallLogs({ size: 1 }, success => {
                  if (success && success.missedCount !== undefined) {
                     store.dispatch(setMissedCallCount(success.missedCount));
                  }
               });
            }
         }

         if (isIncomingNotificationActive()) {
            const users = store.getState().userInfoData?.users || {};
            const callkitData = store.getState().callkitData;
            // Only show missed call if it was DISCONNECTED (hung up) or TIMEOUT (ignored)
            if (
               data?.callStatus === CALLKIT_CALLSTATUS.DISCONNECTED ||
               data?.callStatus === CALLKIT_CALLSTATUS.TIMEOUT
            ) {
               showMissedCallNotification(
                  {
                     from: userId,
                     callType: callkitData.callType,
                     callMode: callkitData.callMode,
                     callerNickName: users[userId]?.nickName || userId,
                     groupId: callkitData.groupId,
                  },
                  users,
               );
            }
         }
         closeIncomingCallNotification();
         const { ongoingCallParticipants, callMode } = store.getState().callkitData;
         const remoteParticipants = ongoingCallParticipants.filter(p => p.userId !== currentUserId);
         const isGroupCall = callMode === CALL_MODES.ONETOMANY || callMode === CALL_MODES.MEET;

         if (
            oldStatus !== undefined &&
            oldStatus !== data.callStatus &&
            (isGroupCall || remoteParticipants.length > 1) &&
            userId !== currentUserId
         ) {
            const { callScreenStatus, ongoingCallParticipants: currentParticipants } = store.getState().callkitData;

            // Show failure toasts if the call is still active for other remote participants (connected, ringing, or calling).
            const otherActiveRemote = currentParticipants.find(
               p =>
                  p.userId !== currentUserId &&
                  p.userId !== userId &&
                  (p.callStatus === CALLKIT_CALLSTATUS.CONNECTED ||
                     p.callStatus === CALLKIT_CALLSTATUS.RINGING ||
                     p.callStatus === CALLKIT_CALLSTATUS.CALLING),
            );

            if (
               callScreenStatus !== CALLKIT_CALLSCREENSTATUS.IDLE &&
               (otherActiveRemote || callMode === CALL_MODES.MEET || callMode === CALL_MODES.ONETOMANY)
            ) {
               const users = store.getState().userInfoData?.users || {};
               const name = users[userId]?.nickName || userId;

               if (data?.callStatus === CALLKIT_CALLSTATUS.ENGAGED) {
                  showErrorToast(`${name} ${i18n.t('common.IS_ENGAGED')}`);
               } else if (data?.callStatus === CALLKIT_CALLSTATUS.REJECTED) {
                  showErrorToast(`${name} ${i18n.t('common.IS_BUSY')}`);
               } else if (data?.callStatus === CALLKIT_CALLSTATUS.TIMEOUT) {
                  showErrorToast(`${name} ${i18n.t('common.IS_NOT_ANSWERING')}`);
               } else if (
                  data?.callStatus === CALLKIT_CALLSTATUS.DISCONNECTED &&
                  (callMode === CALL_MODES.MEET || (callMode === CALL_MODES.ONETOMANY && otherActiveRemote))
               ) {
                  showInfoToast(i18n.t('common.MEET_LEFT', { name }));
               }
            }
         }

         const isBusyStatus =
            data?.callStatus === CALLKIT_CALLSTATUS.ENGAGED || data?.callStatus === CALLKIT_CALLSTATUS.REJECTED;
         const wasBusyStatus = oldStatus === CALLKIT_CALLSTATUS.ENGAGED || oldStatus === CALLKIT_CALLSTATUS.REJECTED;
         const isAnyRemoteBusy =
            !isGroupCall &&
            remoteParticipants.some(
               p => p.callStatus === CALLKIT_CALLSTATUS.ENGAGED || p.callStatus === CALLKIT_CALLSTATUS.REJECTED,
            );

         const timeoutSec = isBusyStatus || wasBusyStatus || isAnyRemoteBusy ? 3000 : 0;
         // Delay the call log refresh slightly so the backend has time to commit the new call log.
         // Skipped for duplicate status events — a repeat of the same event shouldn't queue a
         // second redundant refresh on top of the one the original event already scheduled.
         if (!isDuplicateStatusEvent) {
            setTimeout(() => {
               store.dispatch(triggerCallLogRefresh());
            }, 1500);
         }
         setTimeout(() => {
            if (userId === currentUserId) {
               store.dispatch(resetCallkitData());
            } else {
               store.dispatch(removeParticipantFromCall(userId));
               const { callMode, ongoingCallParticipants } = store.getState().callkitData;
               const remainingRemoteParticipants = ongoingCallParticipants.filter(p => p.userId !== currentUserId);
               if (callMode !== CALL_MODES.MEET && remainingRemoteParticipants.length === 0) {
                  store.dispatch(resetCallkitData());
               }
            }
         }, timeoutSec);
      }
   },

   onAudioLevelChange: () => {
      // intentional no-op: fine-grained audio level changes are not rendered; onActiveSpeakerChange handles speaker detection
   },

   onLocalTracks: localTracks => {
      console.log('onLocalTracks triggered:', localTracks);
      const screenshareTrackInfo = localTracks?.find(t => (t.source || t.track?.source) === 'screen_share');
      const currentUserId = getLocalUserName();
      if (screenshareTrackInfo) {
         const screenUserId = currentUserId + '-screen';
         CallMediaStore.addTrack(screenUserId, screenshareTrackInfo, 'video');
         const exists = store.getState().callkitData.ongoingCallParticipants.some(p => p.userId === screenUserId);
         if (!exists) {
            store.dispatch(
               addParticipantToCall({
                  userId: screenUserId,
                  callStatus: CALLKIT_CALLSTATUS.CONNECTED,
                  isAudioMuted: true, // Audio isn't captured via screen
                  isVideoMuted: false,
               }),
            );
         } else {
            store.dispatch(updateParticipantStatus({ userId: screenUserId, trackUpdated: Date.now() }));
         }
      }
   },
   onScreenShareState: state => {
      console.log('onScreenShareState triggered', state);
      const currentUserId = getLocalUserName();
      const screenUserId = currentUserId + '-screen';
      if (state === CALL_KIT_SCREENSHARE_STATE.STOP) {
         store.dispatch(removeParticipantFromCall(screenUserId));
         CallMediaStore.removeTrack(screenUserId, 'video');
         store.dispatch(updateParticipantStatus({ userId: currentUserId, trackUpdated: Date.now() }));
      } else if (state === CALL_KIT_SCREENSHARE_STATE.RECONNECTING) {
         store.dispatch(updateParticipantStatus({ userId: screenUserId, callStatus: CALLKIT_CALLSTATUS.RECONNECTING }));
      }
   },

   onCallKithelper: {
      getDisplayName: () => {
         return 'Anonymous user ';
      },
      getImageUrl: () => {
         return '';
      },
   },
};
